import React, { useCallback, useState } from 'react';

import { Button, CircularProgress, Dialog, DialogActions, SelectChangeEvent, Typography } from '@mui/material';

// eslint-disable-next-line import/no-webpack-loader-syntax

import { useUpdateVehicleOptions, useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { IConfigurationOptionGroup } from '~/domain/IConfigurationOptionGroup';
import { IConfigurationRule } from '~/domain/IConfigurationRule';
import { IConfigurationSuggestion } from '~/domain/IConfigurationSuggestion';
import { ConfigurationSuggestionDialog } from '~/pages/RequestPage/ConfigurationSuggestionDialog';

import { ConfigurationPackSet } from './ConfigurationPackSet';

interface IConfigurationOptionProps {
  packSet: IConfigurationOptionGroup;
  packSets: IConfigurationOptionGroup[];
  rules: IConfigurationRule[];
}

interface IConfigurationSuggestions {
  suggestChanges: IConfigurationSuggestion[];
  featuresOnAccept: string[];
}

export const ConfigurationOption: React.FC<IConfigurationOptionProps> = (props) => {
  const { packSet, packSets, rules } = props;
  const { packs } = packSet;

  const vehicleOptions = useVehicleOptions();
  const updateVehicleOptions = useUpdateVehicleOptions();
  const { features } = vehicleOptions;
  const [{ suggestChanges = [], featuresOnAccept = [] } = {}, setConfigurationSuggestions] = useState<IConfigurationSuggestions | undefined>();
  const [suggestionWorker, setSuggestionWorker] = useState<Worker>();
  const [isComputingSuggestion, setIsComputingSuggestion] = useState<boolean>(false);

  const selection = packs.find(x => features.includes(x.code));

  const onCancelSuggestionWorker = useCallback(() => {
    suggestionWorker?.terminate();
    setIsComputingSuggestion(false);
    setSuggestionWorker(undefined);
  }, [suggestionWorker]);

  const handleChange = useCallback((e: SelectChangeEvent) => {
    const code = e.target.value;
    const index = features.indexOf(selection?.code || '');
    const newPack = packs.find(x => x.code === code);
    const oldPack = packs.find(x => x.code === features[index]);
    if (index > -1 && features[index] !== code && newPack && oldPack) {
      if (!newPack.isValid) {
        setIsComputingSuggestion(true);
        const worker = suggestionWorker ?? new Worker(new URL('../../../webWorkers/configurationSuggestion.worker.ts', import.meta.url)); 
        if (!suggestionWorker) {
          worker.onmessage = ({data}) => {
            setSuggestionWorker((prev) => {
              prev?.terminate();
              return undefined;
            });
            setIsComputingSuggestion(false);
            
            const { changes, newFeatures } = data;
            setConfigurationSuggestions({
              suggestChanges: changes,
              featuresOnAccept: newFeatures,
            });
          };
          setSuggestionWorker(worker);
        }

        worker.postMessage({
          packSets,
          rules,
          features,
          code,
          packSet,
        });

      } else {
        const newFeatures = [...features];
        newFeatures[index] = code;
        updateVehicleOptions({
          ...vehicleOptions,
          features: newFeatures,
        });
      }
    }
  }, [features, packSet, packSets, packs, rules, selection, updateVehicleOptions, vehicleOptions, suggestionWorker]);

  return (
    <>
      <Dialog
        open={isComputingSuggestion}
        PaperProps={{
          sx: {
            width: 570,
            maxHeight: 544,
            p: '34px 40px 40px 40px',
            alignItems: 'center',
          },
        }}
      >
        <Typography>Trying to find a suitable suggestion...</Typography>
        <CircularProgress />
        <DialogActions>
          <Button autoFocus color="dark" size="small" onClick={onCancelSuggestionWorker}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <ConfigurationPackSet
        label={packSet.name}
        onChange={handleChange}
        packs={packs}
        value={selection?.code ?? packSets[0].code}
      />
      {suggestChanges.length > 0 && (
        <ConfigurationSuggestionDialog
          suggestChanges={suggestChanges}
          onCancel={() => {
            setConfigurationSuggestions(undefined);
          }}
          onAccept={() => {
            updateVehicleOptions({
              ...vehicleOptions,
              features: featuresOnAccept,
            });
            setConfigurationSuggestions(undefined);
          }}
        />
      )}
    </>
  );
};
